import { IconButton, Preview } from "@evergis/ui";
import React, { FC, useCallback } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { reoderList } from "../../../../../../utils/array";
import { ListItem } from "../../../../../LayerList/styled";
import { JSONAttributeValue } from "../../types";
import { AttachmentAddButton } from "./AttachmentAddButton";
import { AttachmentGridItem } from "./AttachmentGridItem";
import { GridItemContainer, GridList, ShowMoreButton } from "./styled";
import { useAttachmentItems } from "./useAttachmentsItems";

export interface AttachmentsGridProps {
  items?: JSONAttributeValue[];
  isEdit: boolean;
  pageSize?: number;
  onChange?: (value?: JSONAttributeValue[]) => void;
}

const PAGE_SIZE = 8;

export const AttachmentsGrid: FC<AttachmentsGridProps> = ({ items, isEdit, pageSize = PAGE_SIZE, onChange }) => {
  const { t } = useTranslation("featureCard");
  const fixedItems = items || [];
  const { onPreview, onAdd, onDelete, onShowMore, page, previewItems, previewOpen, setPreviewOpen } =
    useAttachmentItems(fixedItems, onChange);

  const onDragEnd = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination) return;
      const newAttrs = reoderList(fixedItems, source.index, destination.index);

      onChange(newAttrs);
    },
    [fixedItems, onChange],
  );

  return (
    <>
      <GridList
        onDragEnd={onDragEnd}
        direction={"horizontal"}
        id={"attachments-list"}
        items={fixedItems as unknown as Record<string, unknown>[]}
        getId={item => (item as unknown as JSONAttributeValue)?.link}
        prefix={
          isEdit && (
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            <AttachmentAddButton hiddenItems={fixedItems} onApply={onAdd} offset={[0, -24]}>
              <GridItemContainer>
                <IconButton kind={"feature_add"} />
              </GridItemContainer>
            </AttachmentAddButton>
          )
        }
      >
        {(item, provided, snapshot) => (
          <ListItem
            ref={provided.innerRef}
            {...provided.draggableProps}
            isDragging={snapshot.isDragging}
            {...provided.dragHandleProps}
          >
            <AttachmentGridItem
              item={item as unknown as JSONAttributeValue}
              isEdit={isEdit}
              onDelete={onDelete}
              onPreview={onPreview}
            />
          </ListItem>
        )}
      </GridList>

      {fixedItems?.length > (isEdit ? page * pageSize - 1 : page * pageSize) && (
        <ShowMoreButton onClick={onShowMore}>
          {t("showMore")}{" "}
          {fixedItems?.length - (isEdit ? page * pageSize - 1 : page * pageSize) < pageSize
            ? fixedItems?.length - (isEdit ? page * pageSize - 1 : page * pageSize)
            : pageSize}
        </ShowMoreButton>
      )}
      <Preview images={previewItems} isOpen={previewOpen} onClose={() => setPreviewOpen(false)} />
    </>
  );
};
